import { dictionaryApi as sharedDictionaryApi } from '@/features/dictionaries/api';
import { createAppRequest } from '@/services/request';

export interface ResearchPage<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}
export interface ResearchTopic {
  uuid: string;
  name: string;
  type: string;
  industry: string;
  directions: string[];
  status: string;
  attention: number;
  summary?: string | null;
  currentConclusion?: string | null;
  owner: string;
  ownerMemberUuid: string;
  companyIds: string[];
  materialIds: string[];
  updatedAt: string;
}
export interface ResearchMaterial {
  uuid: string;
  title: string;
  type: string;
  source: string;
  author: string;
  summary?: string | null;
  sourceUrl?: string | null;
  topicIds: string[];
  studyTopicUuid?: string | null;
  companyIds: string[];
  publishedAt?: string | null;
  favorite: boolean;
  updatedAt: string;
}
export interface ResearchCompany {
  uuid: string;
  name: string;
  shortName: string;
  industry: string;
  region: string;
  regionUuid?: string | null;
  round: string;
  source: string;
  followStatus: string;
  owner: string;
  organizationUuid?: string | null;
  topicIds: string[];
  latestEvent: string;
  judgment?: string | null;
  leadUuid?: string | null;
  updatedAt: string;
}
export interface ResearchViewpoint {
  uuid: string;
  topicUuid: string;
  content: string;
  confidence: string;
  status: string;
  nextAction: string;
  authorMemberUuid: string;
  authorName: string;
  updatedAt: string;
}
export interface ResearchTopicDetail extends ResearchTopic {
  materials: ResearchMaterial[];
  viewpoints: ResearchViewpoint[];
  companies: ResearchCompany[];
}
export interface ResearchTopicSummary extends ResearchTopic {
  counts: {
    materials: number;
    facts: number;
    viewpoints: number;
    conclusions: number;
    companies: number;
  };
}
export interface ResearchWorkbench {
  metrics: {
    activeTopics: number;
    keyCompanies: number;
    potentialCompanies: number;
    pendingViewpoints: number;
  };
  topics: ResearchTopic[];
  viewpoints: ResearchViewpoint[];
  companies: ResearchCompany[];
  generatedAt: string;
}
export interface ResearchDashboard {
  metrics: Record<string, number>;
  funnel: Record<string, number>;
  viewpointStatusCounts: Record<string, number>;
  companyIndustryCounts: Record<string, number>;
  generatedAt: string;
}

export type {
  DictionaryGroup as DictionaryItem,
  DictionaryResponse,
} from '@/features/dictionaries/types';

const request = createAppRequest();
export const listResearchTopics = (params?: Record<string, string | number>) =>
  request<ResearchPage<ResearchTopic>>('/bff/research/topics', { params });
export const getResearchTopic = (uuid: string) =>
  request<ResearchTopicDetail>(`/bff/research/topics/${uuid}`);
export const getResearchTopicSummary = (uuid: string) =>
  request<ResearchTopicSummary>(`/bff/research/topics/${uuid}/summary`);
export const listTopicViewpoints = (
  uuid: string,
  params?: Record<string, string | number>,
) =>
  request<ResearchPage<ResearchViewpoint>>(
    `/bff/research/topics/${uuid}/viewpoints`,
    { params },
  );
export const listTopicCompanies = (
  uuid: string,
  params?: Record<string, string | number>,
) =>
  request<ResearchPage<ResearchCompany>>(
    `/bff/research/topics/${uuid}/companies`,
    { params },
  );
export const listTopicMaterials = (
  uuid: string,
  params?: Record<string, string | number>,
) =>
  request<ResearchPage<ResearchMaterial>>(
    `/bff/research/topics/${uuid}/materials`,
    { params },
  );
export const createResearchTopic = (data: Record<string, unknown>) =>
  request<ResearchTopic>('/bff/research/topics', { method: 'POST', data });
export const updateResearchTopic = (
  uuid: string,
  data: Record<string, unknown>,
) =>
  request<ResearchTopic>(`/bff/research/topics/${uuid}`, {
    method: 'PATCH',
    data,
  });
/** 专题协作成员：除负责人外可被授权查看/协作该专题。 */
export type ResearchCollaborator = {
  memberUuid: string;
  createdAt?: string;
};
export const listTopicCollaborators = (topicUuid: string) =>
  request<ResearchCollaborator[]>(
    `/bff/research/topics/${topicUuid}/collaborators`,
  );
export const addTopicCollaborator = (topicUuid: string, memberUuid: string) =>
  request<ResearchCollaborator>(
    `/bff/research/topics/${topicUuid}/collaborators`,
    {
      method: 'POST',
      data: { memberUuid },
    },
  );
export const removeTopicCollaborator = (
  topicUuid: string,
  memberUuid: string,
) =>
  request<unknown>(
    `/bff/research/topics/${topicUuid}/collaborators/${memberUuid}`,
    {
      method: 'DELETE',
    },
  );
export const linkTopicCompany = (topicUuid: string, companyUuid: string) =>
  request<ResearchCompany>(`/bff/research/topics/${topicUuid}/companies`, {
    method: 'POST',
    data: { companyUuid },
  });
export const listResearchMaterials = (
  params?: Record<string, string | number>,
) =>
  request<ResearchPage<ResearchMaterial>>('/bff/research/materials', {
    params,
  });
export const createResearchMaterial = (data: Record<string, unknown>) =>
  request<ResearchMaterial>('/bff/research/materials', {
    method: 'POST',
    data,
  });
export const createResearchViewpoint = (
  topicUuid: string,
  data: Record<string, unknown>,
) =>
  request<ResearchViewpoint>(`/bff/research/topics/${topicUuid}/viewpoints`, {
    method: 'POST',
    data,
  });
export const listResearchCompanies = (
  params?: Record<string, string | number>,
) =>
  request<ResearchPage<ResearchCompany>>('/bff/research/companies', { params });
export const getResearchCompany = (uuid: string) =>
  request<ResearchCompany>(`/bff/research/companies/${uuid}`);
export const createResearchCompany = (data: Record<string, unknown>) =>
  request<ResearchCompany>('/bff/research/companies', { method: 'POST', data });
export const updateResearchCompany = (
  uuid: string,
  data: Record<string, unknown>,
) =>
  request<ResearchCompany>(`/bff/research/companies/${uuid}`, {
    method: 'PATCH',
    data,
  });
export const linkResearchCompanyOrganization = (
  uuid: string,
  organizationUuid: string,
) =>
  request<ResearchCompany>(`/bff/research/companies/${uuid}/organization`, {
    method: 'POST',
    data: { organizationUuid },
  });
export const resolveResearchCompanyOrganization = (uuid: string) =>
  request<ResearchCompany>(
    `/bff/research/companies/${uuid}/resolve-organization`,
    {
      method: 'POST',
    },
  );
export const unlinkResearchCompanyLead = (uuid: string) =>
  request<ResearchCompany>(`/bff/research/companies/${uuid}/unlink-lead`, {
    method: 'POST',
  });
export const convertCompanyToLead = (
  uuid: string,
  data?: Record<string, unknown>,
) =>
  request<{
    alreadyConverted: boolean;
    company: ResearchCompany;
    lead: { id: string; name?: string; status?: string };
  }>(`/bff/research/companies/${uuid}/convert-to-lead`, {
    method: 'POST',
    data: data ?? {},
  });
export const getResearchWorkbench = () =>
  request<ResearchWorkbench>('/bff/research/workbench');
export const getResearchDashboard = () =>
  request<ResearchDashboard>('/bff/research/dashboard');
export const updateResearchViewpoint = (
  uuid: string,
  data: Record<string, unknown>,
) =>
  request<ResearchViewpoint>(`/bff/research/viewpoints/${uuid}`, {
    method: 'PATCH',
    data,
  });
export const getResearchDictionaries = () =>
  sharedDictionaryApi.getByScope('research');
export const updateResearchDictionary = (dictKey: string, values: string[]) =>
  sharedDictionaryApi.update('research', dictKey, { values });

/** 课题管理：研究课题（立项 → 推进事项 → 结题，基础流程）。 */
/** 推进事项分类，与后端 research-study-topic.dto.ts 的 STUDY_PROGRESS_CATEGORIES 一致。 */
export const STUDY_PROGRESS_CATEGORIES = [
  '研究推进',
  '专家访谈',
  '资料收集',
  '会议沟通',
  '风险事项',
  '其他',
];
/** 课题来源（与后端 research-study-topic.dto.ts 的 STUDY_TOPIC_SOURCES 一致）。 */
export const STUDY_TOPIC_SOURCES = ['内部课题', '外部课题', '合作课题', '其他'];
/** 成果形式（可组合选择）。 */
export const STUDY_DELIVERABLE_TYPES = [
  '课题汇报',
  '课题报告',
  '访谈纪要',
  '其他参考资料',
];
export interface ResearchStudyTopic {
  uuid: string;
  name: string;
  type: string;
  source?: string;
  keywords?: string | null;
  isMajor?: number | boolean;
  beginDate?: string | null;
  participants?: string | null;
  background?: string | null;
  expectedResult?: string | null;
  deliverable?: string | null;
  abandonReason?: string | null;
  goal?: string | null;
  status: string;
  plannedEndDate?: string | null;
  owner: string;
  ownerMemberUuid: string;
  closedAt?: string | null;
  closeConclusion?: string | null;
  createdAt?: string;
  updatedAt: string;
}
export interface ResearchStudyTopicDetail extends ResearchStudyTopic {
  progressCount: number;
}
export interface ResearchStudyTopicMetrics {
  total: number;
  inProgress: number;
  closed: number;
  abandoned: number;
  progressTotal: number;
  generatedAt: string;
}
export interface ResearchStudyProgress {
  uuid: string;
  studyTopicUuid: string;
  category: string;
  content: string;
  result?: string | null;
  nextStep?: string;
  progressDate: string;
  authorMemberUuid: string;
  authorName: string;
  createdAt?: string;
  updatedAt: string;
}
export const listResearchStudyTopics = (
  params?: Record<string, string | number>,
) =>
  request<ResearchPage<ResearchStudyTopic>>('/bff/research/study-topics', {
    params,
  });
export const getResearchStudyTopic = (uuid: string) =>
  request<ResearchStudyTopicDetail>(`/bff/research/study-topics/${uuid}`);
export const getResearchStudyTopicMetrics = () =>
  request<ResearchStudyTopicMetrics>('/bff/research/study-topics/metrics');
export const createResearchStudyTopic = (data: Record<string, unknown>) =>
  request<ResearchStudyTopic>('/bff/research/study-topics', {
    method: 'POST',
    data,
  });
export const updateResearchStudyTopic = (
  uuid: string,
  data: Record<string, unknown>,
) =>
  request<ResearchStudyTopic>(`/bff/research/study-topics/${uuid}`, {
    method: 'PATCH',
    data,
  });
export const closeResearchStudyTopic = (
  uuid: string,
  data: Record<string, unknown>,
) =>
  request<ResearchStudyTopic>(`/bff/research/study-topics/${uuid}/close`, {
    method: 'POST',
    data,
  });
export const listResearchStudyProgress = (
  uuid: string,
  params?: Record<string, string | number>,
) =>
  request<ResearchPage<ResearchStudyProgress>>(
    `/bff/research/study-topics/${uuid}/progress`,
    { params },
  );
export const createResearchStudyProgress = (
  uuid: string,
  data: Record<string, unknown>,
) =>
  request<ResearchStudyProgress>(
    `/bff/research/study-topics/${uuid}/progress`,
    {
      method: 'POST',
      data,
    },
  );
export const deleteResearchStudyProgress = (
  uuid: string,
  progressUuid: string,
) =>
  request<unknown>(
    `/bff/research/study-topics/${uuid}/progress/${progressUuid}`,
    {
      method: 'DELETE',
    },
  );

export type ResearchStudyTopicMember = {
  memberUuid: string;
  createdAt?: string;
};
export const listResearchStudyTopicMembers = (uuid: string) =>
  request<ResearchStudyTopicMember[]>(
    `/bff/research/study-topics/${uuid}/members`,
  );
export const addResearchStudyTopicMember = (uuid: string, memberUuid: string) =>
  request<ResearchStudyTopicMember>(
    `/bff/research/study-topics/${uuid}/members`,
    {
      method: 'POST',
      data: { memberUuid },
    },
  );
export const removeResearchStudyTopicMember = (
  uuid: string,
  memberUuid: string,
) =>
  request<unknown>(`/bff/research/study-topics/${uuid}/members/${memberUuid}`, {
    method: 'DELETE',
  });
